import React from "react";
import { Box, Typography, TextField, MenuItem, Chip, Stack } from "@mui/material";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 4,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
  },
};

export default function Basicinfo({
  value,
  onChange,
  deviceTypes,
  provisionableAssets = [],
  selectedAsset = null,
  deviceTypeHelperText = "",
  assetsLoading = false,
  assetError = "",
  allowCustomType = true,
  allowSecurityModeOverride = false,
}) {
  const handleChange = (field, val) => {
    onChange({
      ...value,
      [field]: val,
    });
  };

  return (
    <Box
      sx={{
        borderRadius: 5,
        border: "1px solid rgba(226, 232, 240, 0.95)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={1.25} sx={{ mb: 3 }}>
        <Chip
          label="Step 1"
          sx={{
            width: "fit-content",
            color: "#0F766E",
            backgroundColor: "rgba(20,184,166,0.12)",
            fontWeight: 700,
          }}
        />
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0F172A" }}>
          Device basics
        </Typography>
        <Typography sx={{ color: "#64748B", lineHeight: 1.7 }}>
          Start with the core identity for the device and choose the type profile
          and connection security that should drive provisioning.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1 }}>
            Device Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter a recognizable device name"
            value={value.name}
            onChange={(e) => handleChange("name", e.target.value)}
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1 }}>
            Inventory Asset
          </Typography>
          <TextField
            select
            fullWidth
            value={value.inventory_asset_id || ""}
            onChange={(e) => handleChange("inventory_asset_id", e.target.value)}
            sx={fieldSx}
            helperText={
              assetError ||
              (assetsLoading
                ? "Loading available inventory assets..."
                : "Pick an in-stock asset first. Provisioning will automatically bind it to the device.")
            }
          >
            <MenuItem value="">
              <em>Select inventory asset</em>
            </MenuItem>
            {provisionableAssets.map((asset) => (
              <MenuItem key={asset.id} value={asset.id}>
                {asset.serial_number} | {asset.asset_type} | {asset.model}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1 }}>
            Device Type
          </Typography>
          <TextField
            select
            fullWidth
            value={value.type}
            onChange={(e) => handleChange("type", e.target.value)}
            sx={fieldSx}
            disabled={!value.inventory_asset_id}
            helperText={
              !value.inventory_asset_id
                ? "Select an inventory asset before choosing the device type."
                : deviceTypeHelperText ||
                  "Choose the logical device family that should drive schema and dashboards."
            }
          >
            <MenuItem value="">
              <em>Select device type</em>
            </MenuItem>
            {allowCustomType && <MenuItem value="custom">Custom</MenuItem>}
            {deviceTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
          {selectedAsset && (
            <Typography sx={{ mt: 1, color: "#64748B", fontSize: 13 }}>
              Selected asset: <strong>{selectedAsset.asset_type || "Unknown type"}</strong>
              {selectedAsset.model ? ` · ${selectedAsset.model}` : ""}
            </Typography>
          )}
        </Box>

        {allowSecurityModeOverride && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1 }}>
              Connection Security
            </Typography>
            <TextField
              select
              fullWidth
              value={value.mqtt_security_mode || "PLAINTEXT"}
              onChange={(e) => handleChange("mqtt_security_mode", e.target.value)}
              sx={fieldSx}
              helperText="Choose Plain MQTT for username/password auth or Secure MQTTS for certificate-based provisioning."
            >
              <MenuItem value="PLAINTEXT">Plain MQTT (1883)</MenuItem>
              <MenuItem value="TLS">Secure MQTTS (8883)</MenuItem>
            </TextField>
          </Box>
        )}
      </Box>

      {value.type === "custom" && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1 }}>
            Custom Type
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter custom device type"
            value={value.customType}
            onChange={(e) => handleChange("customType", e.target.value)}
            sx={fieldSx}
          />
        </Box>
      )}
    </Box>
  );
}
